import React from 'react';
import { Link } from 'react-router-dom';

function FinalWordsGuide({ darkMode }) {
  const containerClass = darkMode 
    ? 'min-h-screen bg-gray-900 text-white' 
    : 'min-h-screen bg-gradient-to-br from-rose-50 to-pink-100 text-gray-800';

  const cardClass = darkMode 
    ? 'bg-gray-800 border-gray-700' 
    : 'bg-white border-gray-200';

  return (
    <div className={containerClass}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">🎤</div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
            마지막 한마디로 임팩트 남기기
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            면접 마무리 멘트로 강한 인상 주는 법
          </p>
          <div className={`inline-block px-6 py-3 rounded-full text-sm font-semibold ${darkMode ? 'bg-rose-900 text-rose-200' : 'bg-rose-100 text-rose-800'}`}>
            💡 "마지막 한마디는 게임 체인저가 아니라, 게임 파이널라이저"
          </div>
        </div>

        {/* 마지막 한마디의 진짜 영향력 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">🤔 마지막 한마디의 진짜 영향력</h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-rose-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-rose-600">📊 현실적 관점</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <h4 className="font-bold text-green-600 mb-2">90% 상황</h4>
                  <p className="text-sm">이미 합불이 결정된 상태</p>
                  <p className="text-xs mt-1 opacity-75">→ 좋은 마무리 역할</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-100'}`}>
                  <h4 className="font-bold text-orange-600 mb-2">10% 상황</h4>
                  <p className="text-sm">동점자/접전 상황</p>
                  <p className="text-xs mt-1 opacity-75">→ 진짜 결정 요인!</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-blue-600">🎯 면접관의 진짜 의도</h3>
              <ul className="space-y-2">
                <li>• ⏰ <strong>시간 채우기</strong>: 5-10분 애매하게 남았을 때</li>
                <li>• 🤝 <strong>진정성 확인</strong>: 기본 매너와 회사에 대한 관심도 체크</li>
                <li>• 📝 <strong>마지막 기회 제공</strong>: 놓친 어필 포인트가 있다면 보완</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 언제 진짜 중요해질까 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">⚖️ 언제 진짜 중요해질까?</h2>
          
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
            <h3 className="text-xl font-bold mb-4 text-yellow-600">🔥 결정적 순간들</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-100'}`}>
                  <p className="text-sm"><strong>2명의 지원자 점수가 비슷할 때</strong></p>
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-100'}`}>
                  <p className="text-sm"><strong>실력은 비슷한데 간절함에서 차이날 때</strong></p>
                </div>
              </div>
              <div className="space-y-3">
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                  <p className="text-sm"><strong>면접관이 "누구를 뽑을까?" 고민 중일 때</strong></p>
                </div>
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <p className="text-sm"><strong>회사 문화와 핏(fit)을 마지막으로 확인하고 싶을 때</strong></p>
                </div>
              </div>
            </div>
            <div className="mt-4 text-center">
              <p className="font-semibold text-yellow-600">💡 즉, 준비는 철저히, 기대는 현실적으로!</p>
            </div>
          </div>
        </div>

        {/* 현실적인 시간 가이드 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">⏱️ 현실적인 시간 가이드</h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-green-600">🕐 30초 버전 (가장 일반적)</h3>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'} border-l-4 border-green-500`}>
                <p className="font-medium mb-2">📝 구조:</p>
                <p className="text-sm leading-relaxed">
                  "면접 기회 주셔서 감사합니다. 오늘 대화를 통해 이 회사에 대한 확신이 더욱 커졌습니다. 
                  제 [핵심 역량]으로 팀에 기여하고 싶습니다. 기회를 주신다면 최선을 다하겠습니다."
                </p>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-blue-600">🕑 1분 버전 (시간적 여유가 있을 때)</h3>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'} border-l-4 border-blue-500`}>
                <p className="font-medium mb-2">📝 구조:</p>
                <p className="text-sm leading-relaxed">
                  "면접 기회 주셔서 감사합니다. 특히 말씀해주신 [면접 중 나온 구체적 내용]이 
                  제가 추구하는 가치와 일치한다고 느꼈습니다. 
                  제가 [구체적 경험]을 통해 쌓은 [핵심 역량]으로 [구체적 기여 방안]을 실현하고 싶습니다. 
                  정말 간절한 마음으로 이 기회를 원합니다."
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 상황별 전략 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">🎯 상황별 전략</h2>
          
          <div className="space-y-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-green-600">😊 면접 분위기가 좋았을 때</h3>
              <ul className="space-y-2">
                <li>• 자신감 있게 포부 제시</li>
                <li>• 팀워크와 성장 의지 어필</li>
                <li>• "함께 성장하고 싶다" 메시지</li>
              </ul>
              <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                <p className="text-sm font-medium">예시:</p>
                <p className="text-sm">"오늘 대화를 통해 이 팀에서 함께 성장하고 싶다는 확신이 생겼습니다."</p>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-orange-600">😰 실수했다고 느낄 때</h3>
              <ul className="space-y-2">
                <li>• 겸손하지만 확신 있는 태도</li>
                <li>• 다른 강점으로 만회 시도</li>
                <li>• "배우려는 자세" 강조</li>
              </ul>
              <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-100'}`}>
                <p className="text-sm font-medium">예시:</p>
                <p className="text-sm">"아직 부족한 부분이 있지만, 배우려는 자세로 성장하겠습니다."</p>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="text-xl font-bold mb-4 text-blue-600">🤔 감이 안 올 때</h3>
              <ul className="space-y-2">
                <li>• 진정성과 열정 중심</li>
                <li>• 회사에 대한 관심 표현</li>
                <li>• 안전하고 정중하게</li>
              </ul>
              <div className={`mt-4 p-3 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                <p className="text-sm font-medium">예시:</p>
                <p className="text-sm">"이 회사에서 일하고 싶다는 간절한 마음을 전해드리고 싶습니다."</p>
              </div>
            </div>
          </div>
        </div>

        {/* 동점자 상황에서 빛나는 포인트들 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">💼 동점자 상황에서 빛나는 포인트들</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
              <h3 className="text-lg font-bold mb-4 text-red-600">🔥 간절함 표현</h3>
              <div className="space-y-2">
                <div className={`p-2 rounded ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-100'}`}>
                  <p className="text-sm">"정말 간절한 마음으로"</p>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-100'}`}>
                  <p className="text-sm">"확신이 더욱 커졌습니다"</p>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-100'}`}>
                  <p className="text-sm">"꼭 실현하고 싶습니다"</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
              <h3 className="text-lg font-bold mb-4 text-blue-600">🤝 회사 핏(Fit) 어필</h3>
              <div className="space-y-2">
                <div className={`p-2 rounded ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                  <p className="text-sm">"가치관이 일치한다고 느꼈습니다"</p>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                  <p className="text-sm">"이 팀에서 함께 성장하고 싶습니다"</p>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                  <p className="text-sm">"회사 문화에 잘 적응할 자신 있습니다"</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
              <h3 className="text-lg font-bold mb-4 text-green-600">💪 구체적 기여 방안</h3>
              <div className="space-y-2">
                <div className={`p-2 rounded ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <p className="text-sm">"즉시 도움이 되겠습니다"</p>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <p className="text-sm">"실질적 성과를 만들겠습니다"</p>
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-100'}`}>
                  <p className="text-sm">"[구체적 방법]으로 기여하겠습니다"</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 절대 하면 안 되는 것들 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-red-600">❌ 절대 하면 안 되는 것들</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
                <h3 className="font-bold text-red-600 mb-2">🚫 과도한 기대</h3>
                <p className="text-sm">"마지막 한마디로 뒤집겠다" 마인드</p>
              </div>
              
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
                <h3 className="font-bold text-red-600 mb-2">🚫 뻔한 말</h3>
                <p className="text-sm">"열심히 하겠습니다" (만)<br/>"최선을 다하겠습니다" (만)</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
                <h3 className="font-bold text-red-600 mb-2">🚫 압박하는 말</h3>
                <p className="text-sm">"꼭 뽑아주세요"<br/>"다른 회사보다 여기가 좋아요"</p>
              </div>
              
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'}`}>
                <h3 className="font-bold text-red-600 mb-2">🚫 너무 긴 연설</h3>
                <p className="text-sm">2분 이상은 금물<br/>장황한 설명보다 임팩트 있게</p>
              </div>
            </div>
          </div>
        </div>

        {/* 완벽한 마무리 공식 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">🎯 완벽한 마무리 공식</h2>
          
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-rose-50 to-pink-50'}`}>
            <h3 className="text-xl font-bold mb-4 text-rose-600">[감사] + [확신/공감] + [구체적 기여] + [간절함] + [정중한 마무리]</h3>
            
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-rose-900 bg-opacity-30' : 'bg-white'} border-l-4 border-rose-500`}>
              <p className="text-sm leading-relaxed">
                <span className="font-semibold text-rose-600">"면접 기회 주셔서 감사합니다</span>(감사) + <br/>
                <span className="font-semibold text-blue-600">오늘 말씀해주신 ○○에 대해 큰 공감을 느꼈습니다</span>(확신) + <br/>
                <span className="font-semibold text-green-600">제 [역량]으로 [구체적 기여]를 실현하겠습니다</span>(기여) + <br/>
                <span className="font-semibold text-orange-600">정말 간절한 마음으로 이 기회를 원합니다</span>(간절함) + <br/>
                <span className="font-semibold text-purple-600">귀한 시간 내주셔서 감사합니다</span>(마무리)"
              </p>
            </div>
          </div>
        </div>

        {/* 핵심 메시지 */}
        <div className={`${cardClass} rounded-2xl p-8 mb-8 border shadow-lg`}>
          <h2 className="text-2xl font-bold mb-6 text-rose-600">💡 핵심 메시지</h2>
          
          <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-yellow-50 to-orange-50'}`}>
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-orange-600">
                "마지막 한마디는 게임 체인저가 아니라, 게임 파이널라이저"
              </h3>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-100'}`}>
                  <h4 className="font-bold text-blue-600 mb-2">🎯 90%</h4>
                  <p className="text-sm">좋은 인상으로 마무리하는 역할</p>
                </div>
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-100'}`}>
                  <h4 className="font-bold text-red-600 mb-2">🔥 10%</h4>
                  <p className="text-sm">동점 상황에서 결정적 한 방!</p>
                </div>
              </div>
              
              <div className="mt-6">
                <p className="text-lg font-semibold text-green-600">결론: 준비는 철저히, 기대는 현실적으로! 😊</p>
              </div>
            </div>
          </div>

          {/* 연습 포인트 */}
          <div className={`p-4 rounded-xl ${darkMode ? 'bg-rose-900 bg-opacity-30' : 'bg-rose-100'} mt-6`}>
            <p className="text-sm font-semibold text-rose-600">연습 포인트:</p>
            <ul className="text-sm mt-2 space-y-1">
              <li>• 30초, 1분 버전 모두 준비해서 상황에 맞게 선택</li>
              <li>• 거울 앞에서 반복 연습해서 자연스럽게 말하기</li>
              <li>• 회사별로 조금씩 다르게 커스터마이징</li>
              <li>• 진정성 있게, 과장하지 말고 솔직하게!</li>
            </ul>
          </div>
        </div>

        {/* 네비게이션 버튼 */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Link 
            to="/guide-success" 
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 text-center ${
              darkMode 
                ? 'bg-gray-700 text-white hover:bg-gray-600' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            ← 면접성공가이드 메인으로
          </Link>
          <Link 
            to="/" 
            className="px-8 py-4 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-xl font-semibold hover:from-rose-700 hover:to-pink-700 transition-all duration-300 text-center"
          >
            🏠 홈으로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default FinalWordsGuide;